import singleton from './singleton';

/**
 * Create a controller so that we can cancel requests
 */
const abortController = new AbortController();
const { signal } = abortController;

/**
 * Represents a the base FETCH configuration settings common to most GET requests.
 * See https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch for more options.
 * @param {string} method - The HTTP method used to communicate to the API being requested.
 * @param {string} credentials - This will send the authorization credentials for the user requesting the resource.
 * @param {Object} headers - This HTTP Headers object that you want to send along with the request.
 **/
const baseOptions = {
  method: 'GET',
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json',
  },
  signal: signal,
};

/**
 * This method is used to resolve the first promise that the FETCH method returns. Common things
 * that this method is used for: resolving successful/bad requests, resolving HTTP Status Codes, raising custom errors.
 * @param {Object} response - An HTTP response object. Could be undefined or null if the request was aborted for any reason.
 * @return {Object} - The Promise object for the HTTP call
 **/
function resolve(response: any) {
  if (response) {
    if (response.status >= 200 && response.status < 300) {
      return Promise.resolve(response);
    } else if (response.status === 401) {
      singleton.unauthenticatedCallback?.();
    }
    return Promise.reject(new Error(response.statusText));
  }
  return Promise.reject(new Error('Unable to complete data request.'));
}

/**
 * This is a wrapper function around the FETCH Promise. If additional processing is needed for the HTTP response, it should be done here.
 * @param {string} url - The URL used in the method request. Use this in combination with the toQueryString method.
 * @param {Object} options - The FETCH method HTTP options.
 * @return {Promise} - TODO
 **/
export function request<T>(url: string, options: any): Promise<T> {
  return fetch(prefixUrl(url), options)
    .then(resolve)
    .then(response => {
      // Attempt to resolve .json() promise. If error, return raw response.
      return response?.json().catch(() => response);
    });
}

/**
 * This is a collection of shortcut methods you can use to call HTTP endpoints. By default you can supply the basic method options
 * and get basic operations for each type. This should be sufficient for most calls. If you need to supply overridden options or
 * you would like to change the base options, each convienence method has a overridden options parameter for you to use.
 *
 * There is also a convience method built into this object to do query string construction if you need it.
 **/

/**
 * This is a basic GET method call. It inherits the base options from this library and can be overridden to suit your needs.
 * @param {string} url - The URL used in the method request. Use this in combination with the toQueryString method.
 * @param {Object} [options] - Optional. Used to override the base options for the method. Any options passed in will override the defaults.
 * @return {Object} - The response object from the HTTP call. It will be in JSON format if successful.
 **/
export function get<T>(url: string, options: any = {}): Promise<T> {
  return request<T>(url, Object.assign({}, baseOptions, options));
}

/**
 * This is a basic DELETE method call. It inherits the base options from this library and can be overridden to suit your needs.
 * @param {string} url - The URL used in the method request. Use this in combination with the toQueryString method.
 * @param {Object} [options] - Optional. Used to override the base options for the method. Any options passed in will override the defaults.
 * @return {Object} - The response object from the HTTP call. It will be in JSON format if successful.
 **/
export function del<T>(url: string, data: any = {}, options: any = {}): Promise<T> {
  return request<T>(url, Object.assign({}, baseOptions, { method: 'DELETE', body: JSON.stringify(data) }, options));
}

/**
 * This is a basic PUT method call. It inherits the base options from this library and can be overridden to suit your needs.
 * @param {string} url - The URL used in the method request. Use this in combination with the toQueryString method.
 * @param {Object} [options] - Optional. Used to override the base options for the method. Any options passed in will override the defaults.
 * @return {Object} - The response object from the HTTP call. It will be in JSON format if successful.
 **/
export function put<T>(url: string, data: any = {}, options: any = {}): Promise<T> {
  return request<T>(url, Object.assign({}, baseOptions, { method: 'PUT', body: JSON.stringify(data) }, options));
}

var requestId = 0;

/**
 * This is a basic POST method call. It inherits the base options from this library and can be overridden to suit your needs.
 * @param {string} url - The URL used in the method request. Use this in combination with the toQueryString method.
 * @param {Object} data - Used as the body payload for the method. It will be wrapped in a JSON.stringify() call.
 * @param {Object} [options] - Optional. Used to override the base options for the method. Any options passed in will override the defaults.
 * @return {Object} - The response object from the HTTP call. It will be in JSON format if successful.
 **/
export function post<T>(url: string, data: any = {}, options: any = {}): Promise<T> {
  if (typeof data.requestId === 'boolean' && data.requestId) {
    requestId--;
    data.requestId = requestId;
  }
  return request<T>(url, Object.assign({}, baseOptions, { method: 'POST', body: JSON.stringify(data) }, options));
}

interface ErrorFixer {
  [key: string]: string | number | boolean;
}

export function toQueryString(obj: ErrorFixer) {
  const parts: Array<unknown> = [];
  Object.keys(obj).forEach((p: string) => {
    parts.push(`${encodeURIComponent(p)}=${encodeURIComponent(obj[p])}`);
  });
  return parts.length > 0 ? parts.join('&') : '';
}

export function prefixUrl(url: string) {
  return `${singleton.getUrl()}${url}`;
}

/**
 * Call this method to abort the current request.
 * Behind the scenes, this is using the signal property from a
 *  global AbortController object (https://developer.mozilla.org/en-US/docs/Web/API/AbortController/abort)
 *
 * NOTE: If making multiple, concurrent requests, you can pass in your own instance of a signal to override the global default
 * E.g.
 *  const controller = new AbortController();
 *  const signal = controller.signal;
 *
 *  const url = /SRS/Application/NeoFlow/DecisionSupport/EconLiningResults/1
 *  const data = { featureId: 1 }
 *  const options = { signal: signal }
 *
 *  post(url, data, options);
 *  controller.abort(); //aborts the request
 *
 */
export function abortRequest(): void {
  abortController.abort();
}
